
import React from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  KeyIcon,
  Stack,
  Typography,
  styled,
} from '@datanomik/commons-ui';

import { Building2Line, GoogleFill } from '@datanomik/commons-ui/icons';

import {
  useFormContext,
  withFormProvider,
} from '@/contexts/form';

import Grid from '@/views/common/Grid';
import Switch from '@/views/common/Switch';
import InputField from '@/views/common/InputField';
import AutoComplete from '@/views/common/AutoComplete';

import ChangeAction from './ChangeAction';
import useChangeClient from './hook';

import styles from './styles';

const ChangeClientRaw = ({
  className,
  mode = 'public',
}) => {
  const { formSubmit } = useFormContext();

  const {
    clients,
    mfaData,
    formData,
    showClients,
    handleOnChange,
    handleSubmit,
    openClientModal,
    handleCloseClientModal,
    handleChangeTrustDevice,
  } = useChangeClient();

  if (mode === 'public') {
    const title = showClients ? t('Select Client') : t('Set up Google Authenticator');
    const startAdornment = (
      <KeyIcon color="neutral">
        {showClients ? <Building2Line /> : <GoogleFill />}
      </KeyIcon>
    );

    return (
      <Dialog
        open={openClientModal}
        backdropClose
        maxWidth="xs"
        PaperProps={{
          sx: { minWidth: '400px' },
        }}
      >
        <DialogHeader
          startAdornment={startAdornment}
          text={title}
        />

        <form onSubmit={formSubmit(handleSubmit)} noValidate>
          <DialogContent>
            <Stack p="20px" gap="20px">
              <Grid container>
                {showClients && (
                  <Grid item xs={12}>
                    <AutoComplete
                      onChangeAutocomplete={handleOnChange}
                      value={formData.clientId}
                      name="clientId"
                      label="Active clients"
                      options={clients}
                      itemLabel="company"
                      itemId="id"
                      required
                    />
                  </Grid>
                )}
                {!showClients && (
                  <>
                    {
                      mfaData?.qrCode && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="p5" color="text.500">
                              {t('Please install the Google Authenticator app on your device, and then scan the QR code below to set up two-factor authentication (2FA) for the first time.')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Box textAlign="center">
                              <img src={mfaData?.qrCode} width="150" height="150" alt="qr" />
                            </Box>
                          </Grid>
                        </>
                      )
                    }
                    <Grid item xs={12}>
                      <Typography variant="p5" color="text.500">
                        {t('Please insert here the token generated (6 digits).')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <InputField
                        onChangeInput={handleOnChange}
                        value={formData.mfaToken}
                        name="mfaToken"
                        type="text"
                        label="Token"
                        autoFocus
                        required
                        validate={{
                          maxLength: (text) => text.length <= 128 || 'The maximum size is 128 characters',
                        }}
                        inputProps={{
                          autoComplete: 'off',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box className="dn-flex-between dn-flex-middle">
                        <Typography variant="p5" color="text.500">
                          {t('Trust this device for 30 days')}
                        </Typography>
                        <Switch
                          checked={formData.trustDevice}
                          onChange={handleChangeTrustDevice}
                        />
                      </Box>
                    </Grid>
                    {
                      formData.trustDevice && (
                        <Grid item xs={12}>
                          <InputField
                            onChangeInput={handleOnChange}
                            value={formData.device}
                            name="device"
                            type="text"
                            label="Device name"
                            required
                            inputProps={{
                              autoComplete: 'off',
                            }}
                          />
                        </Grid>
                      )
                    }
                  </>
                )}
              </Grid>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseClientModal}
              variant="outlined"
              color="black"
              fullWidth
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
            >
              {t('Continue')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  return (
    <ChangeAction
      className={className}
      clients={clients}
      activeClient={formData.clientId}
    />
  );
};

ChangeClientRaw.propTypes = {
  className: PropTypes.string,
  mode: PropTypes.string,
};

const ChangeClient = styled(ChangeClientRaw)`${styles}`;

ChangeClient.propTypes = ChangeClientRaw.propTypes;

export default withFormProvider(ChangeClient);
